export const ROUTE_PATHS = {
  HOME: '/',
  DEPARTMENTS: '/departments',
  ADMISSIONS: '/admissions',
  FACULTY: '/faculty',
  NEWS: '/news',
  REGULATIONS: '/regulations',
  CONTACT: '/contact',
  ACCREDITATIONS: '/accreditations',
  PROGRAM_DETAIL: '/programs/:programId',
  ADMIN: '/admin',
} as const;

export const PROGRAM_IDS = {
  DIPLOMA: 'diploma',
  ADVANCED_DIPLOMA: 'advanced-diploma',
  BACHELOR: 'bachelor',
  BACHELOR_COMPLETION: 'bachelor-completion',
} as const;

export interface Department {
  id: string;
  nameAr: string;
  nameEn: string;
  icon: string;
  studentCount: number;
  degreeLevels: string[];
  description: string;
  courses: string[];
  careers: string[];
  requirements: string[];
}

export interface NewsItem {
  id: string;
  title: string;
  date: string;
  category: 'أخبار الكلية' | 'إنجازات الطلاب' | 'فعاليات' | 'إعلانات';
  excerpt: string;
  image: string;
}

export interface FacultyMember {
  id: string;
  name: string;
  title: string;
  specialization: string;
  experience: number;
  department: string;
  avatar: string;
}

export interface CollegeStats {
  students: number;
  faculty: number;
  departments: number;
  degreeLevels: number;
}

export interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  type: 'معرض' | 'محاضرة' | 'توظيف' | 'ورشة عمل';
}

export interface Regulation {
  id: string;
  title: string;
  type: 'لائحة' | 'دليل' | 'نموذج';
  pages: number;
  year: number;
  summary: string;
}

export const departments: Department[] = [
  {
    id: 'software-engineering',
    nameAr: 'هندسة البرمجيات',
    nameEn: 'Software Engineering',
    icon: 'Code',
    studentCount: 55,
    degreeLevels: ['دبلوم متوسط (سنتان)', 'دبلوم عالي (3 سنوات)', 'بكالوريوس (4 سنوات)', 'استكمال البكالوريوس لخريجي الدبلوم العالي (سنة ونصف)'],
    description: 'تخصص هندسة البرمجيات يركز على تطوير وتصميم الأنظمة البرمجية الحديثة باستخدام أحدث التقنيات والمنهجيات.',
    courses: [
      'برمجة الويب المتقدمة',
      'هندسة البرمجيات',
      'قواعد البيانات',
      'تطوير تطبيقات الموبايل',
      'الذكاء الاصطناعي',
      'أمن المعلومات',
      'هندسة المتطلبات',
      'اختبار البرمجيات'
    ],
    careers: [
      'مطور برمجيات',
      'مهندس برمجيات',
      'مطور تطبيقات موبايل',
      'مطور ويب',
      'محلل أنظمة',
      'مهندس ضمان الجودة'
    ],
    requirements: [
      'شهادة الثانوية العامة (علمي)',
      'معدل لا يقل عن 65%',
      'اجتياز اختبار القبول',
      'إجادة اللغة الإنجليزية'
    ]
  },
  {
    id: 'network-engineering',
    nameAr: 'هندسة الشبكات والاتصالات',
    nameEn: 'Network Engineering & Communications',
    icon: 'Network',
    studentCount: 45,
    degreeLevels: ['دبلوم متوسط (سنتان)', 'دبلوم عالي (3 سنوات)', 'بكالوريوس (4 سنوات)', 'استكمال البكالوريوس لخريجي الدبلوم العالي (سنة ونصف)'],
    description: 'تخصص هندسة الشبكات يهتم بتصميم وإدارة وصيانة شبكات الحاسوب والاتصالات الحديثة.',
    courses: [
      'أساسيات الشبكات',
      'بروتوكولات الإنترنت',
      'أمن الشبكات',
      'الشبكات اللاسلكية',
      'إدارة الخوادم',
      'الحوسبة السحابية',
      'شبكات المؤسسات',
      'الاتصالات الرقمية'
    ],
    careers: [
      'مهندس شبكات',
      'مدير أنظمة',
      'مهندس أمن شبكات',
      'مهندس حلول سحابية',
      'مستشار شبكات',
      'مهندس دعم فني'
    ],
    requirements: [
      'شهادة الثانوية العامة (علمي)',
      'معدل لا يقل عن 65%',
      'اجتياز اختبار القبول',
      'معرفة أساسية بالحاسوب'
    ]
  },
  {
    id: 'business-administration',
    nameAr: 'إدارة الأعمال',
    nameEn: 'Business Administration',
    icon: 'Briefcase',
    studentCount: 65,
    degreeLevels: ['دبلوم متوسط (سنتان)', 'دبلوم عالي (3 سنوات)', 'بكالوريوس (4 سنوات)', 'استكمال البكالوريوس لخريجي الدبلوم العالي (سنة ونصف)'],
    description: 'تخصص إدارة الأعمال يزود الطلاب بالمهارات الإدارية والقيادية اللازمة لإدارة المؤسسات بكفاءة.',
    courses: [
      'مبادئ الإدارة',
      'إدارة الموارد البشرية',
      'التسويق',
      'إدارة المشاريع',
      'ريادة الأعمال',
      'السلوك التنظيمي',
      'الإدارة الاستراتيجية',
      'إدارة العمليات'
    ],
    careers: [
      'مدير أعمال',
      'مدير موارد بشرية',
      'مدير تسويق',
      'مدير مشاريع',
      'مستشار إداري',
      'رائد أعمال'
    ],
    requirements: [
      'شهادة الثانوية العامة',
      'معدل لا يقل عن 60%',
      'اجتياز المقابلة الشخصية',
      'مهارات تواصل جيدة'
    ]
  },
  {
    id: 'financial-accounting',
    nameAr: 'المحاسبة المالية',
    nameEn: 'Financial Accounting',
    icon: 'Calculator',
    studentCount: 60,
    degreeLevels: ['دبلوم متوسط (سنتان)', 'دبلوم عالي (3 سنوات)', 'بكالوريوس (4 سنوات)', 'استكمال البكالوريوس لخريجي الدبلوم العالي (سنة ونصف)'],
    description: 'تخصص المحاسبة المالية يركز على إعداد وتحليل القوائم المالية والتقارير المحاسبية.',
    courses: [
      'مبادئ المحاسبة',
      'المحاسبة المالية المتقدمة',
      'محاسبة التكاليف',
      'المراجعة والتدقيق',
      'المحاسبة الضريبية',
      'نظم المعلومات المحاسبية',
      'التحليل المالي',
      'المحاسبة الإدارية'
    ],
    careers: [
      'محاسب مالي',
      'مدقق حسابات',
      'محلل مالي',
      'مدير حسابات',
      'مستشار ضريبي',
      'محاسب تكاليف'
    ],
    requirements: [
      'شهادة الثانوية العامة',
      'معدل لا يقل عن 60%',
      'مهارات رياضية جيدة',
      'اجتياز اختبار القبول'
    ]
  },
  {
    id: 'finance-investment',
    nameAr: 'التمويل والاستثمار',
    nameEn: 'Finance and Investment',
    icon: 'TrendingUp',
    studentCount: 40,
    degreeLevels: ['دبلوم متوسط (سنتان)', 'دبلوم عالي (3 سنوات)', 'بكالوريوس (4 سنوات)', 'استكمال البكالوريوس لخريجي الدبلوم العالي (سنة ونصف)'],
    description: 'تخصص التمويل والاستثمار يهتم بإدارة الأموال والاستثمارات وتحليل الأسواق المالية.',
    courses: [
      'مبادئ التمويل',
      'إدارة الاستثمار',
      'الأسواق المالية',
      'التحليل المالي',
      'إدارة المحافظ الاستثمارية',
      'التمويل الإسلامي',
      'إدارة المخاطر المالية',
      'الاقتصاد المالي'
    ],
    careers: [
      'محلل استثماري',
      'مدير محفظة',
      'مستشار مالي',
      'محلل أسواق مالية',
      'مدير استثمار',
      'مستشار تمويل'
    ],
    requirements: [
      'شهادة الثانوية العامة',
      'معدل لا يقل عن 65%',
      'مهارات تحليلية قوية',
      'إجادة اللغة الإنجليزية'
    ]
  },
  {
    id: 'legal-administration',
    nameAr: 'الإدارة والتنظيم القانوني',
    nameEn: 'Legal Administration',
    icon: 'Scale',
    studentCount: 35,
    degreeLevels: ['دبلوم متوسط (سنتان)', 'دبلوم عالي (3 سنوات)', 'بكالوريوس (4 سنوات)', 'استكمال البكالوريوس لخريجي الدبلوم العالي (سنة ونصف)'],
    description: 'تخصص الإدارة والتنظيم القانوني يجمع بين المعرفة القانونية والمهارات الإدارية.',
    courses: [
      'مبادئ القانون',
      'القانون التجاري',
      'قانون العمل',
      'القانون الإداري',
      'العقود والالتزامات',
      'الإدارة القانونية',
      'التحكيم والوساطة',
      'حقوق الإنسان'
    ],
    careers: [
      'مساعد قانوني',
      'مدير شؤون قانونية',
      'مستشار قانوني',
      'محلل امتثال',
      'منسق عقود',
      'مدير علاقات حكومية'
    ],
    requirements: [
      'شهادة الثانوية العامة',
      'معدل لا يقل عن 60%',
      'مهارات كتابية قوية',
      'اجتياز المقابلة الشخصية'
    ]
  }
];

export const newsItems: NewsItem[] = [
  {
    id: 'news-1',
    title: 'افتتاح مختبر الذكاء الاصطناعي الجديد',
    date: '2026-04-15',
    category: 'أخبار الكلية',
    excerpt: 'تم افتتاح مختبر الذكاء الاصطناعي المجهز بأحدث التقنيات لخدمة طلاب قسم هندسة البرمجيات.',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800'
  },
  {
    id: 'news-2',
    title: 'طلاب الكلية يفوزون بالمركز الأول في مسابقة البرمجة الوطنية',
    date: '2026-04-10',
    category: 'إنجازات الطلاب',
    excerpt: 'حقق فريق من طلاب قسم هندسة البرمجيات المركز الأول في المسابقة الوطنية للبرمجة.',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800'
  },
  {
    id: 'news-3',
    title: 'ندوة حول ريادة الأعمال والابتكار',
    date: '2026-04-05',
    category: 'فعاليات',
    excerpt: 'نظمت الكلية ندوة حول ريادة الأعمال بحضور نخبة من رواد الأعمال الليبيين.',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800'
  },
  {
    id: 'news-4',
    title: 'بدء التسجيل للفصل الدراسي الربيعي 2026',
    date: '2026-03-25',
    category: 'إعلانات',
    excerpt: 'تعلن الكلية عن فتح باب التسجيل للفصل الدراسي الربيعي 2026 لجميع التخصصات.',
    image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800'
  },
  {
    id: 'news-5',
    title: 'توقيع اتفاقية تعاون مع شركات تقنية عالمية',
    date: '2026-03-20',
    category: 'أخبار الكلية',
    excerpt: 'وقعت الكلية اتفاقيات تعاون مع شركات تقنية رائدة لتوفير فرص تدريب للطلاب.',
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800'
  },
  {
    id: 'news-6',
    title: 'تخريج الدفعة العاشرة من طلاب الكلية',
    date: '2026-03-15',
    category: 'فعاليات',
    excerpt: 'احتفلت الكلية بتخريج 250 طالباً وطالبة من مختلف التخصصات في حفل مهيب.',
    image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800'
  }
];

export const facultyMembers: FacultyMember[] = [
  {
    id: 'faculty-1',
    name: 'د. أحمد محمد الصادق',
    title: 'أستاذ مشارك',
    specialization: 'هندسة البرمجيات',
    experience: 15,
    department: 'هندسة البرمجيات',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400'
  },
  {
    id: 'faculty-2',
    name: 'د. فاطمة علي المبروك',
    title: 'أستاذ مساعد',
    specialization: 'أمن المعلومات',
    experience: 10,
    department: 'هندسة الشبكات والاتصالات',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400'
  },
  {
    id: 'faculty-3',
    name: 'د. خالد سالم العبيدي',
    title: 'أستاذ',
    specialization: 'إدارة الأعمال',
    experience: 20,
    department: 'إدارة الأعمال',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400'
  },
  {
    id: 'faculty-4',
    name: 'د. سارة محمود الزروق',
    title: 'أستاذ مشارك',
    specialization: 'المحاسبة المالية',
    experience: 12,
    department: 'المحاسبة المالية',
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400'
  },
  {
    id: 'faculty-5',
    name: 'د. عمر حسن الفيتوري',
    title: 'أستاذ مساعد',
    specialization: 'التمويل والاستثمار',
    experience: 8,
    department: 'التمويل والاستثمار',
    avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400'
  },
  {
    id: 'faculty-6',
    name: 'د. نادية إبراهيم القذافي',
    title: 'أستاذ مشارك',
    specialization: 'القانون التجاري',
    experience: 14,
    department: 'الإدارة والتنظيم القانوني',
    avatar: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=400'
  },
  {
    id: 'faculty-7',
    name: 'د. محمد عبدالله الشريف',
    title: 'أستاذ مساعد',
    specialization: 'الشبكات اللاسلكية',
    experience: 9,
    department: 'هندسة الشبكات والاتصالات',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400'
  },
  {
    id: 'faculty-8',
    name: 'د. ليلى سعيد المقريف',
    title: 'أستاذ',
    specialization: 'ريادة الأعمال',
    experience: 18,
    department: 'إدارة الأعمال',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400'
  }
];

export const events: Event[] = [
  {
    id: 'event-1',
    title: 'معرض التوظيف السنوي',
    date: '2026-05-10',
    time: '09:00 - 16:00',
    location: 'قاعة المؤتمرات الرئيسية',
    type: 'توظيف'
  },
  {
    id: 'event-2',
    title: 'محاضرة: مستقبل الذكاء الاصطناعي',
    date: '2026-05-05',
    time: '14:00 - 16:00',
    location: 'المدرج الكبير',
    type: 'محاضرة'
  },
  {
    id: 'event-3',
    title: 'ورشة عمل: تطوير تطبيقات الموبايل',
    date: '2026-04-28',
    time: '10:00 - 14:00',
    location: 'مختبر البرمجة',
    type: 'ورشة عمل'
  },
  {
    id: 'event-4',
    title: 'معرض مشاريع التخرج',
    date: '2026-05-15',
    time: '10:00 - 18:00',
    location: 'قاعة المعارض',
    type: 'معرض'
  },
  {
    id: 'event-5',
    title: 'محاضرة: الأمن السيبراني في العصر الرقمي',
    date: '2026-05-20',
    time: '13:00 - 15:00',
    location: 'قاعة 201',
    type: 'محاضرة'
  }
];

export const regulations: Regulation[] = [
  {
    id: 'reg-1',
    title: 'لائحة الدراسة والامتحانات',
    type: 'لائحة',
    pages: 45,
    year: 2026,
    summary: 'تحدد هذه اللائحة نظام الدراسة والامتحانات والتقييم والحضور والغياب وشروط النجاح والرسوب.'
  },
  {
    id: 'reg-2',
    title: 'دليل الطالب',
    type: 'دليل',
    pages: 60,
    year: 2026,
    summary: 'دليل شامل يتضمن معلومات عن الكلية والأقسام والخدمات الطلابية والإجراءات الأكاديمية.'
  },
  {
    id: 'reg-3',
    title: 'لائحة الانضباط الطلابي',
    type: 'لائحة',
    pages: 25,
    year: 2026,
    summary: 'تنظم هذه اللائحة السلوك الطلابي والعقوبات التأديبية وإجراءات التظلم.'
  },
  {
    id: 'reg-4',
    title: 'دليل أعضاء هيئة التدريس',
    type: 'دليل',
    pages: 50,
    year: 2026,
    summary: 'يتضمن معلومات عن واجبات وحقوق أعضاء هيئة التدريس والإجراءات الأكاديمية والإدارية.'
  }
];